import type { createImagesApi } from '~/entities/image/api/images-api'

type ImagesApi = Pick<ReturnType<typeof createImagesApi>, 'uploadMultiple' | 'reorder' | 'delete'>

/**
 * Merges freshly uploaded image ids into the existing order, placing each
 * uploaded id at its requested insert position and filling the remaining
 * slots with existing ids in their current order.
 */
function mergeImageOrder(
  existingIds: string[],
  uploadedIds: string[],
  insertPositions: number[],
): string[] {
  const positionMap = new Map<number, string>()
  uploadedIds.forEach((id, index) => {
    const position = insertPositions[index]
    if (position !== undefined) {
      positionMap.set(position, id)
    }
  })

  const finalOrder: string[] = []
  let existingIndex = 0

  for (let i = 0; i < existingIds.length + uploadedIds.length; i++) {
    const insertedId = positionMap.get(i)
    if (insertedId !== undefined) {
      finalOrder.push(insertedId)
    } else if (existingIndex < existingIds.length) {
      finalOrder.push(existingIds[existingIndex])
      existingIndex++
    }
  }

  return finalOrder
}

/**
 * Uploads the staged files into their chosen slots, then deletes the saved images they replace.
 * The deletes run last because a live event or listing refuses to lose its only image.
 */
export async function saveStagedImages(
  api: ImagesApi,
  entityId: string,
  staged: {
    existingIds: string[]
    files: File[]
    insertPositions: number[]
    removedIds: string[]
  },
): Promise<void> {
  const removed = new Set(staged.removedIds)
  const keptIds = staged.existingIds.filter((id) => !removed.has(id))
  const uploaded = await api.uploadMultiple(entityId, staged.files)
  const uploadedIds = uploaded.map((image) => image.id)
  const finalOrder = mergeImageOrder(keptIds, uploadedIds, staged.insertPositions)
  const appendedOrder = [...keptIds, ...uploadedIds]
  if (finalOrder.some((id, index) => id !== appendedOrder[index])) {
    await api.reorder(entityId, finalOrder)
  }
  for (const id of staged.removedIds) await api.delete(id)
}
